/**
 * Spectator View Component
 * View for non-playing team members to watch the quiz
 */

import React, { useMemo } from "react";
import {
  View,
  Text,
  ScrollView,
  Dimensions,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import LottieView from "lottie-react-native";
import { useThemeColors } from "@/providers/ThemeProvider";
import { getLottieByKey, getLottieSource } from "@/lib/lottie-registry";
import {
  LiveQuizSession,
  LiveQuizParticipant,
  QuizQuestion,
  RankingEntry,
} from "../types";

const { width: SCREEN_WIDTH } = Dimensions.get("window");

interface SpectatorViewProps {
  /** Current session */
  session: LiveQuizSession;
  /** All participants */
  participants: LiveQuizParticipant[];
  /** Current question (if playing) */
  currentQuestion: QuizQuestion | null;
  /** Time remaining */
  timeRemaining: number;
  /** Rankings */
  rankings: RankingEntry[];
  /** Quiz name */
  quizName: string;
  /** Quiz color */
  quizColor?: string;
}

/**
 * Spectator view for watching the quiz
 */
export const SpectatorView: React.FC<SpectatorViewProps> = ({
  session,
  participants,
  currentQuestion,
  timeRemaining,
  rankings,
  quizName,
  quizColor = "#8B5CF6",
}) => {
  const colors = useThemeColors();
  
  /**
   * Get team's participant entry
   */
  const myTeamParticipant = useMemo(() => {
    // This would be passed in through context or props
    return participants[0];
  }, [participants]);
  
  /**
   * Render lobby state
   */
  const renderLobby = () => (
    <View className="flex-1 items-center justify-center p-6">
      <LottieView
        source={getLottieSource("alarm_clock")}
        autoPlay
        loop
        style={{ width: 150, height: 150 }}
      />
      <Text 
        className="text-2xl font-bold mt-6 text-center"
        style={{ color: colors.text }}
      >
        {quizName}
      </Text>
      <Text 
        className="text-base mt-2 text-center"
        style={{ color: colors.textSecondary }}
      >
        Wachten tot de quiz begint...
      </Text>
      
      {/* Participating teams */}
      <View className="mt-8 w-full">
        <Text 
          className="text-lg font-semibold mb-4 text-center"
          style={{ color: colors.text }}
        >
          Teams ({participants.length})
        </Text>
        
        <View className="flex-row flex-wrap justify-center gap-3">
          {participants.map((participant) => {
            const mascotLottie = getLottieByKey(participant.mascot_key);
            
            return (
              <View 
                key={participant.id}
                className="items-center p-3 rounded-2xl"
                style={{ 
                  backgroundColor: colors.surface,
                  width: (SCREEN_WIDTH - 60) / 3,
                }}
              >
                {mascotLottie?.source ? (
                  <LottieView
                    source={mascotLottie.source}
                    autoPlay
                    loop
                    style={{ width: 50, height: 50 }}
                  />
                ) : (
                  <View className="w-12 h-12 items-center justify-center">
                    <Ionicons name="paw" size={28} color={colors.textTertiary} />
                  </View>
                )}
                <Text 
                  className="text-xs mt-2 font-medium text-center"
                  style={{ color: colors.text }}
                  numberOfLines={1}
                >
                  {participant.team?.name || "Team"}
                </Text>
                {participant.is_ready && (
                  <Ionicons name="checkmark-circle" size={16} color="#10B981" />
                )}
              </View>
            );
          })}
        </View>
      </View>
    </View>
  );
  
  /**
   * Render playing state (spectator view)
   */
  const renderPlaying = () => (
    <View className="flex-1">
      {/* Question header */}
      <View 
        className="px-4 py-3 border-b"
        style={{ 
          backgroundColor: colors.surface,
          borderBottomColor: colors.border,
        }}
      >
        <View className="flex-row items-center justify-between">
          <View 
            className="px-3 py-1 rounded-full"
            style={{ backgroundColor: quizColor + "30" }}
          >
            <Text className="font-bold" style={{ color: quizColor }}>
              Vraag {(session.current_question_index || 0) + 1}/{session.total_questions}
            </Text>
          </View>
          
          <View 
            className="flex-row items-center px-3 py-1 rounded-full"
            style={{ backgroundColor: timeRemaining <= 5 ? "#EF444420" : "#10B98120" }}
          >
            <Ionicons 
              name="time" 
              size={16} 
              color={timeRemaining <= 5 ? "#EF4444" : "#10B981"} 
            />
            <Text 
              className="ml-1 font-bold"
              style={{ color: timeRemaining <= 5 ? "#EF4444" : "#10B981" }}
            >
              {timeRemaining}s
            </Text>
          </View>
        </View>
      </View>
      
      {/* Spectator message */}
      <View 
        className="mx-4 mt-4 p-4 rounded-2xl items-center"
        style={{ backgroundColor: colors.surface }}
      >
        <View className="w-12 h-12 rounded-full bg-violet-100 dark:bg-violet-900/30 items-center justify-center mb-2">
          <Ionicons name="eye" size={24} color={quizColor} />
        </View>
        <Text className="font-semibold text-center" style={{ color: colors.text }}>
          Je bent toeschouwer
        </Text>
        <Text 
          className="text-sm text-center mt-1"
          style={{ color: colors.textSecondary }}
        >
          Kijk naar de TV voor de vraag. Alleen de teamcaptain kan antwoorden.
        </Text>
      </View>
      
      {/* Live rankings during play */}
      <View className="flex-1 p-4">
        <Text 
          className="font-semibold mb-3"
          style={{ color: colors.text }}
        >
          Live Stand
        </Text>
        
        <ScrollView 
          showsVerticalScrollIndicator={false}
          className="flex-1"
        >
          {rankings.map((entry, index) => {
            const getMedalEmoji = (rank: number) => {
              if (rank === 1) return "🥇";
              if (rank === 2) return "🥈";
              if (rank === 3) return "🥉";
              return `#${rank}`;
            };
            
            return (
              <View 
                key={entry.participant.id}
                className="flex-row items-center p-3 rounded-xl mb-2"
                style={{ 
                  backgroundColor: index < 3 ? quizColor + "10" : colors.surface,
                }}
              >
                <Text className="text-lg w-10">{getMedalEmoji(entry.rank)}</Text>
                
                {entry.mascotLottie?.source && (
                  <View className="mr-3">
                    <LottieView
                      source={entry.mascotLottie.source}
                      autoPlay
                      loop
                      style={{ width: 40, height: 40 }}
                    />
                  </View>
                )}
                
                <View className="flex-1">
                  <Text className="font-semibold" style={{ color: colors.text }}>
                    {entry.participant.team?.name || "Team"}
                  </Text>
                  <Text className="text-xs" style={{ color: colors.textSecondary }}>
                    {entry.correct_answers} correct
                  </Text>
                </View>
                
                <Text 
                  className="text-lg font-bold"
                  style={{ color: "#F59E0B" }}
                >
                  {entry.total_score}
                </Text>
              </View>
            );
          })}
        </ScrollView>
      </View>
    </View>
  );
  
  /**
   * Render rankings state
   */
  const renderRankings = () => (
    <View className="flex-1 items-center justify-center p-6">
      <LottieView
        source={getLottieSource("head_with_crown_emoji")}
        autoPlay
        loop
        style={{ width: 100, height: 100 }}
      />
      <Text 
        className="text-2xl font-bold mt-4"
        style={{ color: colors.text }}
      >
        🏆 Tussenstand
      </Text>
      
      <View className="w-full mt-6">
        {rankings.slice(0, 5).map((entry) => {
          const getMedalEmoji = (rank: number) => {
            if (rank === 1) return "🥇";
            if (rank === 2) return "🥈";
            if (rank === 3) return "🥉";
            return `#${rank}`;
          };
          
          return (
            <View 
              key={entry.participant.id}
              className="flex-row items-center p-4 rounded-2xl mb-3"
              style={{ 
                backgroundColor: entry.rank <= 3 ? quizColor + "20" : colors.surface,
              }}
            >
              <Text className="text-2xl w-12 text-center">
                {getMedalEmoji(entry.rank)}
              </Text>
              
              {entry.mascotLottie?.source && (
                <View className="mr-4">
                  <LottieView
                    source={entry.mascotLottie.source}
                    autoPlay
                    loop
                    style={{ width: 50, height: 50 }}
                  />
                </View>
              )}
              
              <View className="flex-1">
                <Text 
                  className="font-bold text-lg"
                  style={{ color: colors.text }}
                >
                  {entry.participant.team?.name || "Team"}
                </Text>
                <Text style={{ color: colors.textSecondary }}>
                  {entry.correct_answers} correct
                </Text>
              </View>
              
              <Text 
                className="text-2xl font-bold"
                style={{ color: "#F59E0B" }}
              >
                {entry.total_score}
              </Text>
            </View>
          );
        })}
      </View>
    </View>
  );
  
  /**
   * Render completed state
   */
  const renderCompleted = () => {
    const winner = rankings[0];
    
    return (
      <View className="flex-1 items-center justify-center p-6">
        <LottieView
          source={getLottieSource("confetti")}
          autoPlay
          loop={false}
          style={{ 
            position: "absolute",
            width: SCREEN_WIDTH,
            height: SCREEN_WIDTH,
          }}
        />
        
        <Text 
          className="text-3xl font-bold text-center"
          style={{ color: colors.text }}
        >
          🎉 Quiz Afgelopen!
        </Text>
        
        {winner && (
          <View className="items-center mt-8">
            <Text 
              className="text-xl font-semibold mb-4"
              style={{ color: colors.textSecondary }}
            >
              Winnaar
            </Text>
            
            {winner.mascotLottie?.source && (
              <LottieView
                source={winner.mascotLottie.source}
                autoPlay
                loop
                style={{ width: 120, height: 120 }}
              />
            )}
            
            <Text 
              className="text-2xl font-bold mt-4"
              style={{ color: colors.text }}
            >
              {winner.participant.team?.name || "Team"}
            </Text>
            
            <View 
              className="px-6 py-3 rounded-full mt-3"
              style={{ backgroundColor: "#F59E0B20" }}
            >
              <Text 
                className="text-xl font-bold"
                style={{ color: "#F59E0B" }}
              >
                {winner.total_score} punten
              </Text>
            </View>
          </View>
        )}
        
        {/* Other placements */}
        <View className="flex-row mt-8 gap-4">
          {rankings.slice(1, 3).map((entry) => (
            <View 
              key={entry.participant.id}
              className="items-center p-4 rounded-2xl"
              style={{ 
                backgroundColor: colors.surface,
                width: (SCREEN_WIDTH - 56) / 2,
              }}
            >
              <Text className="text-xl mb-2">
                {entry.rank === 2 ? "🥈" : "🥉"}
              </Text>
              {entry.mascotLottie?.source && (
                <LottieView
                  source={entry.mascotLottie.source}
                  autoPlay
                  loop
                  style={{ width: 60, height: 60 }}
                />
              )}
              <Text 
                className="font-semibold mt-2"
                style={{ color: colors.text }}
              >
                {entry.participant.team?.name || "Team"}
              </Text>
              <Text style={{ color: "#F59E0B" }}>
                {entry.total_score} pts
              </Text>
            </View>
          ))}
        </View>
      </View>
    );
  };
  
  return (
    <View className="flex-1" style={{ backgroundColor: colors.background }}>
      {session.status === "lobby" && renderLobby()}
      {session.status === "playing" && renderPlaying()}
      {session.status === "showing_ranking" && renderRankings()}
      {session.status === "completed" && renderCompleted()}
    </View>
  );
};

export default SpectatorView;

